"use client";

import { useRefs, type Refs } from "@mediamonks/react-kit";
import {
  MouseEvent,
  type ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import classes from "./C100Navigation.module.scss";
import {
  AppLogo,
  HorizontalNavigation,
  NavigationMenuGroup,
  NavigationMenuItem,
  NavigationQuickActionGroup,
  VerticalNavigation,
} from "@hawkins/components";
import { M1Button, M1ButtonProps } from "../../molecules/M1Button/M1Button.tsx";
import { useViewportStateMedium } from "../../../hooks/useViewportState.ts";
import { Clickable } from "../../abstract/Clickable/Clickable.tsx";
import { useLenis } from "lenis/react";
import { useRouter } from "@tanstack/react-router";

export type C100NavigationProps = {
  pageName: ReactNode;
  currentLocale: string;
  links?: Array<M1ButtonProps>;
  callToAction: M1ButtonProps;
};

export type C100NavigationRefs = Refs<{
  self: HTMLDivElement;
}>;

export function C100Navigation({
  pageName,
  currentLocale,
  links = [],
  callToAction,
}: C100NavigationProps): ReactElement {
  const refs = useRefs<C100NavigationRefs>();
  const lenis = useLenis();
  const router = useRouter();

  const isMediumViewport = useViewportStateMedium();
  const [mobileNavigationIsVisible, setMobileNavigationIsVisible] =
    useState(false);

  const toggleMobileNavigation = useCallback(() => {
    setMobileNavigationIsVisible((prev) => !prev);
  }, []);

  const onNavigationItemClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>, href: string) => {
      const url = new URL(href, window.location.origin);

      // Strip trailing slash from the pathname to avoid double slashes.
      const stripTrailingSlash = (pathname: string) =>
        pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;

      // Get the target path and the current path to compare them
      const targetPath = stripTrailingSlash(url.pathname);
      const currentPath = stripTrailingSlash(router.state.location.pathname);

      // If the target path is not the current path or there is no hash, return
      if (targetPath !== currentPath || !url.hash) {
        return;
      }

      // Close the mobile navigation menu
      event.preventDefault();
      setMobileNavigationIsVisible(false);

      // If the hash is not the same as the current hash, update the URL
      // to reflect the new section
      // `pushState` doesn't trigger any scroll on its own.
      if (window.location.hash !== url.hash) {
        window.history.pushState(null, "", url.hash);
      }

      // Scroll to the target section using absolute pixel position
      // (and `force: true`) so Lenis always smooth-scrolls
      // and isn't blocked by a previous in-flight animation.
      const target = document.querySelector(url.hash);
      if (target && lenis) {
        const top = target.getBoundingClientRect().top + window.scrollY;
        lenis.scrollTo(top, { force: true });
      } else if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    [lenis, router],
  );

  const onLogoClick = useCallback(() => {
    if (router.state.location.pathname !== `/${currentLocale.toLowerCase()}`) {
      router.navigate({ to: `/${currentLocale.toLowerCase()}` });
    }
    lenis?.scrollTo(0);
  }, [currentLocale, lenis, router]);

  useEffect(() => {
    if (isMediumViewport) {
      setMobileNavigationIsVisible(false);
    }
  }, [isMediumViewport]);

  useEffect(() => {
    if (mobileNavigationIsVisible) {
      lenis?.stop();
    } else {
      lenis?.start();
    }
  }, [mobileNavigationIsVisible, lenis]);

  const callToActionGroup = useMemo(
    () => (
      <NavigationQuickActionGroup>
        {isMediumViewport ? (
          <M1Button variant="primary" size="medium" {...callToAction} />
        ) : (
          <Clickable
            className={classes.navigationToggleButton}
            onClick={toggleMobileNavigation}
            data-is-visible={mobileNavigationIsVisible}
          >
            <span />
          </Clickable>
        )}
      </NavigationQuickActionGroup>
    ),
    [
      callToAction,
      isMediumViewport,
      mobileNavigationIsVisible,
      toggleMobileNavigation,
    ],
  );

  const navigationItemsGroup = useMemo(
    () =>
      isMediumViewport ? (
        <NavigationMenuGroup>
          {links.map((link) => (
            <NavigationMenuItem
              key={link.href}
              href={link.href}
              onClick={(event: MouseEvent<HTMLAnchorElement>) =>
                onNavigationItemClick(event, link.href ?? "#")
              }
            >
              {link.children}
            </NavigationMenuItem>
          ))}
        </NavigationMenuGroup>
      ) : (
        links.map((link) => (
          // @ts-expect-error This is a valid "onClick" prop for the M1Button component
          <M1Button
            key={link.href}
            variant="link"
            size="large"
            href={link.href}
            onClick={(event: MouseEvent<HTMLAnchorElement>) =>
              onNavigationItemClick(event, link.href ?? "#")
            }
          >
            {link.children}
          </M1Button>
        ))
      ),
    [isMediumViewport, links, onNavigationItemClick],
  );

  return (
    <>
      <HorizontalNavigation
        className={classes.c100Navigation}
        ref={refs.self}
        logo={
          <Clickable className={classes.logo} onClick={onLogoClick}>
            <AppLogo
              name={<>{pageName}</>}
              classes={{ textContainer: classes.textContainer }}
              studioLogo
            />
          </Clickable>
        }
        quickActions={callToActionGroup}
      >
        {isMediumViewport && navigationItemsGroup}
      </HorizontalNavigation>
      {mobileNavigationIsVisible && (
        <VerticalNavigation
          key={Date.now()}
          className={classes.mobileNavigation}
          classes={{
            childrenContainer: classes.mobileNavigationChildrenContainer,
            mainContent: classes.mobileNavigationMainContent,
          }}
        >
          {navigationItemsGroup}
        </VerticalNavigation>
      )}
    </>
  );
}
